const API_BASE = '/api'

export interface SystemStatus {
  gpu_count: number
  gpu_available: boolean
  active_jobs: number
  queued_jobs: number
  total_completed: number
  total_samples_processed: number
}

export interface EnsembleSettings {
  models: string[]
  merge_strategy: 'snr_weighted' | 'average' | 'max_energy'
}

export interface TranscriptionConfig {
  // Basic (11 parameters)
  resolution: number
  bpm: number | null
  smartSounds: boolean
  unifiedSelector: boolean
  humanize: boolean
  detectChords: boolean
  swing: boolean
  electronic: boolean
  quantize: number
  duration: boolean
  merge: boolean
  // Drums (10 parameters)
  larsnet: boolean
  onsetFilter: number
  onsetKick: number | null
  onsetSnare: number | null
  onsetHihat: number | null
  velocity: boolean
  soundMatch: boolean
  enhanced: boolean
  logLevel: number
  explain: boolean
  // Melody (9 parameters)
  melodyMode: string
  melodyRegister: string
  minConfidence: number | null
  crepe: string
  splitBass: boolean
  clean: boolean
  noMerge: boolean
  splitStems: boolean
  splitThreshold: number
  // Advanced (10 parameters)
  v9: boolean
  humanFeel: boolean
  textHint: string | null
  trackName: string | null
  artist: string | null
  parseBpm: boolean
  noConfidence: boolean
  logFile: string | null
}

export interface JobConfig {
  model: string
  gpus: number  // Always 1 (Lambda uses single GPU)
  max_retries: number
  quality_preset: string | null  // Always 'ultra' (Lambda hardcoded)
  ensemble_settings: EnsembleSettings | null
  transcription: TranscriptionConfig
  enable_transcription: boolean
}

export interface JobProgress {
  total_samples: number
  completed: number
  failed: number
  pending: number
  progress_percent: number
  samples_per_sec: number
  eta_seconds: number
}

export interface JobCosts {
  gpu_hours: number
  estimated_cost_usd: number
  hourly_rate: number
  cost_per_sample: number
}

export interface Job {
  job_id: string
  name: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  config: JobConfig
  progress: JobProgress
  costs: JobCosts
  created_at: string
  updated_at: string
  input_files: string[]
  results: SampleResult[]
  error: string | null
}

export interface JobListItem {
  job_id: string
  name: string
  status: string
  progress_percent: number
  total_samples: number
  completed: number
  cost_usd: number
  created_at: string
}

export interface TranscriptionStats {
  notes: number
  duration_sec: number
  instruments?: number
}

export interface TranscriptionInfo {
  stem_name: string
  midi_path: string
  stats?: TranscriptionStats
}

export interface StemInfo {
  name: string
  file_path: string
  file_size_bytes: number
  sub_stems?: StemInfo[]  // For hierarchical stems (e.g., drums -> kick, snare, hihat)
  transcription?: TranscriptionInfo  // MIDI transcription for melodic stems
}

export interface StemQualityMetrics {
  spectral_clarity: number
  cross_stem_isolation: number
  energy_focus: number
  harmonic_preservation: number
  overall_score: number
  raw_snr: number
}

export interface StemComparison {
  snr: number
  weight: number
  is_winner: boolean
  file_path: string
  quality_metrics?: StemQualityMetrics
}

export interface EnsembleComparisonData {
  models: string[]
  merge_strategy: string
  stems: Record<string, Record<string, StemComparison>>
}

export interface SampleResult {
  sample_name: string
  stems: StemInfo[]
  transcriptions?: TranscriptionInfo[]  // Standalone transcriptions (e.g., drum elements)
  models_used?: string[]
  merge_strategy?: string
  ensemble_comparison?: EnsembleComparisonData
}

export interface UploadResponse {
  filename: string
  file_size_bytes: number
  duration_sec: number | null
  format: string
}

export interface EstimateResponse {
  estimated_time_hours: number
  estimated_cost_usd: number
  cost_per_sample: number
  throughput_samples_per_min: number
}

export interface ModelInfo {
  id: string
  name: string
  stems: number
  memory_gb: number
  time_per_sample: number
  sdr: number
  best_for: string
  framework: string
}

// Quality is hardcoded to ULTRA on Lambda worker (10 shifts, 0.75 overlap, 24-bit)

export async function getSystemStatus(): Promise<SystemStatus> {
  const res = await fetch(`${API_BASE}/status`)
  if (!res.ok) throw new Error('Failed to fetch status')
  return res.json()
}

export async function uploadFiles(files: File[]): Promise<UploadResponse[]> {
  const formData = new FormData()
  files.forEach(file => formData.append('files', file))

  const res = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    body: formData
  })

  if (!res.ok) throw new Error('Upload failed')
  return res.json()
}

export async function getEstimate(
  fileCount: number,
  model: string,
  gpus: number,
  qualityPreset?: string | null,
  ensembleModels?: string[]
): Promise<EstimateResponse> {
  const res = await fetch(`${API_BASE}/estimate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      file_count: fileCount,
      model,
      gpus,
      gpu_type: 'A100-80GB',
      quality_preset: qualityPreset || null,
      ensemble_models: ensembleModels || null
    })
  })

  if (!res.ok) throw new Error('Estimate failed')
  return res.json()
}

export async function createJob(
  name: string,
  config: Partial<JobConfig>,
  files: string[] = []
): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, config, files })
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.detail || 'Failed to create job')
  }
  return res.json()
}

export async function listJobs(): Promise<JobListItem[]> {
  const res = await fetch(`${API_BASE}/jobs`)
  if (!res.ok) throw new Error('Failed to fetch jobs')
  return res.json()
}

export async function getJob(jobId: string): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`)
  if (!res.ok) throw new Error('Job not found')
  return res.json()
}

export async function cancelJob(jobId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/cancel`, { method: 'POST' })
  if (!res.ok) throw new Error('Failed to cancel job')
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to delete job')
}

export async function getJobResults(jobId: string): Promise<{ results: SampleResult[] }> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/results`)
  if (!res.ok) throw new Error('Failed to fetch results')
  return res.json()
}

export async function getModels(): Promise<{ models: ModelInfo[] }> {
  const res = await fetch(`${API_BASE}/models`)
  if (!res.ok) throw new Error('Failed to fetch models')
  return res.json()
}

// getQualityPresets removed - Lambda worker uses hardcoded ULTRA quality

export function createWebSocket(onMessage: (data: unknown) => void): WebSocket {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const ws = new WebSocket(`${protocol}//${window.location.host}/ws`)

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      onMessage(data)
    } catch {
      console.error('Failed to parse WebSocket message')
    }
  }

  return ws
}

export function getStemsZipUrl(jobId: string, sampleName: string, folderName?: string): string {
  const params = folderName ? `?folder_name=${encodeURIComponent(folderName)}` : ''
  return `${API_BASE}/download/${encodeURIComponent(jobId)}/${encodeURIComponent(sampleName)}${params}`
}

export function getMidiZipUrl(jobId: string, sampleName: string, folderName?: string): string {
  const params = folderName ? `?folder_name=${encodeURIComponent(folderName)}` : ''
  return `${API_BASE}/download/${encodeURIComponent(jobId)}/${encodeURIComponent(sampleName)}/midi${params}`
}
